import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Inbox, Mail, Linkedin, MessageSquare, Sparkles, ChevronRight, Check, X as XIcon } from "lucide-react";

const CHANNELS = [
  { v: "all", l: "All", icon: Inbox },
  { v: "email", l: "Email", icon: Mail },
  { v: "linkedin", l: "LinkedIn", icon: Linkedin },
];

const STATUSES = ["pending", "approved", "dismissed", "all"];

const INTENT_COLOR = {
  positive: "bg-[hsl(var(--success))] text-white",
  meeting_request: "bg-primary text-primary-foreground",
  objection: "bg-[hsl(var(--warning))] text-foreground",
  neutral: "bg-secondary text-foreground",
  negative: "bg-destructive text-destructive-foreground",
  unsubscribe: "bg-destructive text-destructive-foreground",
};

export default function Triage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [channelFilter, setChannelFilter] = useState("all");
  const [classifyingId, setClassifyingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, cts] = await Promise.all([
        api.get("/triage", { params: { status: statusFilter, channel: channelFilter === "all" ? undefined : channelFilter } }),
        api.get("/triage/counts"),
      ]);
      setItems(list.data);
      setCounts(cts.data);
    } catch {
      toast.error("Failed to load triage");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter, channelFilter]);

  const updateStatus = async (inbox_id, status, opts = {}) => {
    try {
      const { data } = await api.patch(`/triage/${inbox_id}`, { status, ...opts });
      if (data?.send_result?.skipped && data.send_result.reason === "instantly_ai_already_replied") {
        toast.success("Marked handled — Instantly AI already replied");
      } else if (data?.reply_sent) {
        toast.success("Reply sent via Resend");
      } else {
        toast.success(status);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const approveForceSend = async (inbox_id) => {
    await updateStatus(inbox_id, "approved", { force_send: true });
  };

  const classifyLinkedIn = async (inbox_id) => {
    setClassifyingId(inbox_id);
    try {
      const { data } = await api.post(`/triage/${inbox_id}/classify`);
      toast.success(`Classified as ${data.classification?.intent}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Classify failed");
    } finally {
      setClassifyingId(null);
    }
  };

  const copyReply = async (text) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); }
  };

  return (
    <Layout title="Unified Inbox" subtitle="Triage" actions={
      <div className="flex items-center gap-1 bg-card border border-border p-1" style={{ borderRadius: 2 }}>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 text-xs capitalize ${statusFilter === s ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`} style={{ borderRadius: 2 }} data-testid={`triage-status-${s}`}>{s}</button>
        ))}
      </div>
    }>
      {/* Channel selector */}
      <div className="grid grid-cols-3 gap-3 mb-6 max-w-2xl">
        {CHANNELS.map(({ v, l, icon: Icon }) => {
          const active = channelFilter === v;
          const c = v === "email" ? counts?.email : v === "linkedin" ? counts?.linkedin : null;
          const total = v === "all" ? counts?.total_pending : c?.pending;
          return (
            <button
              key={v}
              onClick={() => setChannelFilter(v)}
              className={`p-4 border text-left transition-all ${active ? "border-foreground bg-card" : "border-border bg-card/40 hover:bg-card"}`}
              style={{ borderRadius: 2 }}
              data-testid={`triage-channel-${v}`}
            >
              <div className="flex items-center justify-between mb-1">
                <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.6} />
                <div className="font-mono text-xs text-muted-foreground">{total ?? 0} pending</div>
              </div>
              <div className="text-sm font-medium">{l}</div>
            </button>
          );
        })}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-card border border-border p-12 text-center" style={{ borderRadius: 2 }} data-testid="triage-empty">
          <Inbox className="h-6 w-6 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-sm text-muted-foreground">
            {statusFilter === "pending" ? "No pending replies. New email replies (IMAP / Instantly webhook) and LinkedIn DMs land here for AI triage." : "No items in this view."}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => {
          const ChannelIcon = it.channel === "email" ? Mail : it.channel === "linkedin" ? Linkedin : MessageSquare;
          const intentClass = INTENT_COLOR[it.intent] || "bg-secondary text-foreground";
          return (
            <div key={it.inbox_id} className="bg-card border border-border p-5" style={{ borderRadius: 2 }} data-testid={`triage-item-${it.inbox_id}`}>
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 flex items-center justify-center shrink-0 ${it.channel === "email" ? "bg-secondary text-foreground" : "bg-primary/10 text-primary"}`} style={{ borderRadius: 2 }}>
                    <ChannelIcon className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-medium">{it.lead?.full_name || "Unknown lead"}</span>
                      <span className="text-xs text-muted-foreground">· {it.lead?.title} · {it.lead?.company}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                      {it.channel === "email" ? `Email · ${it.source}` : "LinkedIn DM"} · {new Date(it.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                  </div>
                </div>
                {it.intent ? (
                  <div className={`text-[10px] uppercase tracking-widest font-mono px-2 py-1 ${intentClass}`} style={{ borderRadius: 2 }}>
                    {it.intent} · {Math.round((it.confidence || 0) * 100)}%
                  </div>
                ) : (
                  it.channel === "linkedin" && (
                    <Button size="sm" variant="outline" onClick={() => classifyLinkedIn(it.inbox_id)} disabled={classifyingId === it.inbox_id} data-testid={`triage-classify-${it.inbox_id}`} style={{ borderRadius: 2 }}>
                      <Sparkles className="h-3 w-3 mr-1" strokeWidth={1.5} /> {classifyingId === it.inbox_id ? "Classifying…" : "AI classify"}
                    </Button>
                  )
                )}
              </div>

              {it.subject && <div className="text-sm font-medium mb-2">{it.subject}</div>}
              <div className="text-sm whitespace-pre-wrap border-l-2 border-border pl-3 text-foreground/85 mb-3">{it.body || it.preview}</div>

              {it.reasoning && (
                <div className="text-xs text-muted-foreground italic mb-2">→ {it.reasoning}</div>
              )}

              {it.suggested_reply && (
                <div className="bg-secondary/40 p-3 mt-3 mb-3 font-mono text-xs whitespace-pre-wrap" style={{ borderRadius: 2 }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="overline">Suggested reply</div>
                    <button onClick={() => copyReply(it.suggested_reply)} className="text-xs text-muted-foreground hover:text-foreground" data-testid={`triage-copy-${it.inbox_id}`}>Copy</button>
                  </div>
                  {it.suggested_reply}
                </div>
              )}

              {it.instantly_ai_replied_at && it.instantly_ai_replied_at > (it.created_at || "") && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 p-3 my-3 text-xs flex items-start gap-2" style={{ borderRadius: 2 }} data-testid={`triage-instantly-ai-badge-${it.inbox_id}`}>
                  <Sparkles className="h-3.5 w-3.5 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" strokeWidth={1.8} />
                  <div>
                    <div className="font-medium text-amber-900 dark:text-amber-100">Instantly AI Sales Agent already replied</div>
                    <div className="text-amber-800/80 dark:text-amber-200/80 mt-0.5">
                      Replied {new Date(it.instantly_ai_replied_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}. Clicking Approve will mark this handled without sending a second reply.
                    </div>
                  </div>
                </div>
              )}

              {it.status === "pending" && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-border flex-wrap">
                  <Button size="sm" onClick={() => updateStatus(it.inbox_id, "approved")} data-testid={`triage-approve-${it.inbox_id}`} style={{ borderRadius: 2 }}>
                    <Check className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} />
                    {it.instantly_ai_replied_at && it.instantly_ai_replied_at > (it.created_at || "") ? "Mark handled" : "Approve & send"}
                  </Button>
                  {it.instantly_ai_replied_at && it.instantly_ai_replied_at > (it.created_at || "") && (
                    <Button size="sm" variant="outline" onClick={() => approveForceSend(it.inbox_id)} data-testid={`triage-force-send-${it.inbox_id}`} style={{ borderRadius: 2 }}>
                      Send anyway
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => updateStatus(it.inbox_id, "dismissed")} data-testid={`triage-dismiss-${it.inbox_id}`} style={{ borderRadius: 2 }}>
                    <XIcon className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Dismiss
                  </Button>
                  <div className="flex-1" />
                  {it.lead?.lead_id && (
                    <button onClick={() => window.location.assign(`/leads?focus=${it.lead.lead_id}`)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center" data-testid={`triage-open-${it.inbox_id}`}>
                      Open lead <ChevronRight className="h-3.5 w-3.5 ml-0.5" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              )}

              {it.status === "approved" && it.sent_via && (
                <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border" data-testid={`triage-sent-status-${it.inbox_id}`}>
                  Reply sent via {it.sent_via} {it.reply_sent_at ? `· ${new Date(it.reply_sent_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}` : ""}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
